import React, { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { Table, TableBody, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DataTableHeadCell } from 'components/shared/data-table/data-table.componet';
import { ActionGridInput } from 'models/order-input/shared';
import isEqual from 'lodash/isEqual';
import ActionsDataGridRow from './actions-data-grid-row.component';
import { emptyActionInput, isActionRowEmpty } from './form-helper';
import { ActionMaterial, OrderLabAction } from 'models/order-input/lab-order';
import ActionMaterialDialog from '../action-material-dialog/action-material-dialog.component';
import MaterialMacroDialog from '../material-macro-dialog/material-macro-dialog.component';
import { OrderDetailsContext } from 'pages/order-details/order-details-context';
import { MaterialMacro, MaterialMacroOption } from 'models/material-macro';
import { ActionSelectionPayload, actionSelectionLabel } from './action-selection';

type ActionsDataGridProps = {
    actions: ActionGridInput[];
    priceList: OrderLabAction[];
    onChange?: (actions: ActionGridInput[], isGridValid: boolean) => void;
    warranty?: boolean;
    isTioTandvard?: boolean;
    isActionBasedOrder: boolean;
    disableForActionBased?: boolean;
    /** The quantity a new row starts at (one per tooth on per-tooth work); 1 by default. */
    defaultQuantity?: number;
    /** The quantity of a per-tooth action: the construction's teeth, as the server counts them. */
    perToothQuantity?: number;
    // The selection travels with the material so the parent has both the quantity the
    // material inherits and the action it belongs to.
    onActionMaterialAdded?: (material: ActionMaterial, selection: ActionSelectionPayload) => void;
    onMaterialMacroAdded?: (materialMacro: MaterialMacro, selection: ActionSelectionPayload) => void;
    // Reports every action selection that has macros/actionMaterials attached, so a
    // parent that persists on its own Save button can defer prompting until after that.
    onActionSelected?: (selection: ActionSelectionPayload) => void;
    // When true this grid opens no dialogs itself - the parent drives them.
    deferMaterialDialogs?: boolean;
};
const ActionsDataGrid: FC<ActionsDataGridProps> = ({
    actions,
    priceList,
    onChange,
    warranty,
    isTioTandvard,
    isActionBasedOrder,
    disableForActionBased = false,
    defaultQuantity,
    perToothQuantity,
    onActionMaterialAdded,
    onMaterialMacroAdded,
    onActionSelected,
    deferMaterialDialogs = false,
}) => {
    const { t: translate } = useTranslation(['common', 'orders', 'actions-materials']);
    const [actionStates, setActionStates] = useState<ActionGridInput[]>([]);
    // The rows the grid last committed. handleRowChange reads and writes it synchronously, so
    // the parent's onChange never runs inside a setState updater: React replays queued
    // updaters while rendering, and a parent update from there looped until "Maximum update
    // depth exceeded" (typing a free-text line into the lab order grid did it).
    const actionStatesRef = useRef<ActionGridInput[]>([]);
    const commitActionStates = (rows: ActionGridInput[]) => {
        actionStatesRef.current = rows;
        setActionStates(rows);
    };
    const [actionMaterialDialogOpen, setActionMaterialDialogOpen] = useState(false);
    const [currentActionMaterials, setCurrentActionMaterials] = useState<ActionMaterial[]>([]);
    const [isActionMaterialRequired, setIsActionMaterialRequired] = useState(false);
    const [materialMacroDialogOpen, setMaterialMacroDialogOpen] = useState(false);
    const [currentMaterialMacroOptions, setCurrentMaterialMacroOptions] = useState<MaterialMacroOption[]>([]);
    // The selection that opened the dialogs - supplies the product label and the
    // quantity the added materials inherit, whether they come from a macro or from
    // the action's own actionMaterials.
    const [currentSelection, setCurrentSelection] = useState<ActionSelectionPayload>();
    const { order } = useContext(OrderDetailsContext);

    useEffect(() => {
        commitActionStates([]);
    }, [order?._id]);

    useEffect(() => {
        commitActionStates([...actions, { ...emptyActionInput() }]);
    }, [actions]);

    const handleRowChange = (
        index: number,
        action: ActionGridInput,
        // Absent entirely on the SIMPLE client price list, which does not select the field.
        actionMaterials: ActionMaterial[] | undefined,
        isActionMaterialRequired: boolean,
        materialMacroOptions?: MaterialMacroOption[],
    ) => {
        const prev = actionStatesRef.current;
        // Prevent unnecessary updates if nothing changed
        const prevRow = prev[index];
        if (prevRow && isEqual(prevRow, action)) {
            return;
        }
        let updated = [...prev];
        updated[index] = action;
        const isLastRow = index === prev.length - 1;
        if (isLastRow && !isActionRowEmpty(action)) {
            updated = [...updated, { ...emptyActionInput() }];
        }

        if (updated.length > 1) {
            // Remove empty rows if they are not the last row
            updated = updated.filter((row, idx) => idx === updated.length - 1 || !isActionRowEmpty(row));
            if (updated.length === 0) {
                updated = [emptyActionInput()];
            }
        }

        commitActionStates(updated);
        if (onChange) {
            const noneEmptyRows = updated.slice(0, -1).filter((row) => row && !isActionRowEmpty(row));
            onChange(
                noneEmptyRows,
                noneEmptyRows.every((row) => row.isValid),
            );
        }

        // Any linked macro opens the picker, selectable or not - it shows the unusable
        // ones disabled with a reason, so the lab learns why rather than seeing nothing.
        const hasMaterialMacros = Boolean(materialMacroOptions && materialMacroOptions.length > 0);
        const hasActionMaterials = Boolean(actionMaterials && actionMaterials.length > 0);

        if (hasMaterialMacros || hasActionMaterials) {
            const selection: ActionSelectionPayload = {
                actionId: action.actionId,
                actionCode: action.actionCode || '',
                description: action.description || '',
                quantity: action.quantity || '1',
                materialMacroOptions: materialMacroOptions || [],
                actionMaterials: actionMaterials || [],
                isActionMaterialRequired,
            };
            if (onActionSelected) {
                onActionSelected(selection);
            }
            // Macro-linked materials take priority - offer the macro picker first, then
            // fall through to the existing single-material picker exactly as before.
            // Skipped entirely when the parent defers prompting until after its own save.
            if (!deferMaterialDialogs) {
                setCurrentSelection(selection);
                // Never store undefined here - the SIMPLE client price list does not
                // return actionMaterials at all, and handleAddMaterialMacro reads .length.
                setCurrentActionMaterials(actionMaterials || []);
                setIsActionMaterialRequired(isActionMaterialRequired);
                if (hasMaterialMacros) {
                    setCurrentMaterialMacroOptions(materialMacroOptions || []);
                    setMaterialMacroDialogOpen(true);
                } else {
                    setActionMaterialDialogOpen(true);
                }
            }
        }
    };

    const handleAddActionMaterial = (material: ActionMaterial | undefined) => {
        if (material && currentSelection && onActionMaterialAdded) {
            onActionMaterialAdded(material, currentSelection);
        }
        setActionMaterialDialogOpen(false);
    };

    const handleAddMaterialMacro = (materialMacro: MaterialMacro | undefined) => {
        if (materialMacro && currentSelection && onMaterialMacroAdded) {
            onMaterialMacroAdded(materialMacro, currentSelection);
        }
        setMaterialMacroDialogOpen(false);
        // Continue the existing actionMaterials flow unchanged, whether a macro was picked or not.
        if (currentActionMaterials.length > 0) {
            setActionMaterialDialogOpen(true);
        }
    };

    return (
        <Fragment>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <DataTableHeadCell sx={{ width: 100 }}>
                                {translate('actions-materials:ACTION')}
                            </DataTableHeadCell>
                            <DataTableHeadCell>{translate('common:DESCRIPTION')}</DataTableHeadCell>
                            <DataTableHeadCell sx={{ width: 150 }}>{translate('orders:CATEGORY')}</DataTableHeadCell>
                            <DataTableHeadCell sx={{ width: 70 }} alignRight>
                                {translate('orders:QUANTITY')}
                            </DataTableHeadCell>
                            <DataTableHeadCell sx={{ width: 90 }} alignRight>
                                {translate('orders:UNIT_PRICE')}
                            </DataTableHeadCell>
                            <Tooltip title={translate('TOTAL_EXCL_VAT').toString()} placement="top">
                                <DataTableHeadCell sx={{ width: 120 }} alignRight>
                                    {translate('orders:TOTAL_EXCL_VAT')}
                                </DataTableHeadCell>
                            </Tooltip>
                            <DataTableHeadCell sx={{ width: 80 }} alignRight>
                                {translate('orders:VAT_PERCENT')}
                            </DataTableHeadCell>
                            <Tooltip title={translate('TOTAL_INCL_VAT').toString()} placement="top">
                                <DataTableHeadCell sx={{ width: 120 }} align="right">
                                    {translate('orders:TOTAL_INCL_VAT')}
                                </DataTableHeadCell>
                            </Tooltip>
                            <DataTableHeadCell sx={{ width: 61 }} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {actionStates.map((row, index) => {
                            const isLast = index === actionStates.length - 1;
                            const isPlaceholderRow = isLast && isActionRowEmpty(row);
                            return (
                                <ActionsDataGridRow
                                    key={index}
                                    action={row}
                                    priceList={priceList}
                                    isPlaceholderRow={isPlaceholderRow}
                                    onChange={(
                                        action,
                                        actionMaterials,
                                        isActionMaterialRequired,
                                        materialMacroOptions,
                                    ) =>
                                        handleRowChange(
                                            index,
                                            action,
                                            actionMaterials,
                                            isActionMaterialRequired,
                                            materialMacroOptions,
                                        )
                                    }
                                    warranty={warranty}
                                    isTioTandvard={isTioTandvard}
                                    isActionBasedOrder={isActionBasedOrder}
                                    disableForActionBased={disableForActionBased}
                                    defaultQuantity={defaultQuantity}
                                    perToothQuantity={perToothQuantity}
                                    dataTest={`actions-data-grid.row-${index}`}
                                />
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <MaterialMacroDialog
                open={materialMacroDialogOpen}
                onClose={() => setMaterialMacroDialogOpen(false)}
                materialMacroOptions={currentMaterialMacroOptions}
                onAddMaterialMacro={(selectedMaterialMacro) => {
                    handleAddMaterialMacro(selectedMaterialMacro);
                }}
                actionLabel={currentSelection ? actionSelectionLabel(currentSelection) : undefined}
            />
            <ActionMaterialDialog
                open={actionMaterialDialogOpen}
                onClose={() => setActionMaterialDialogOpen(false)}
                actionMaterials={currentActionMaterials}
                onAddMaterial={(selectedMaterial) => {
                    handleAddActionMaterial(selectedMaterial);
                }}
                isMaterialRequired={isActionMaterialRequired}
                actionLabel={currentSelection ? actionSelectionLabel(currentSelection) : undefined}
            />
        </Fragment>
    );
};

export default ActionsDataGrid;
